import type { CartItem } from '../types'

const resolveImage = (item: CartItem) => item.productImageUrl || ''

export const CartDrawerItems = ({
  sortedItems,
  busy,
  activeItemId,
  formatCurrency,
  removeLabel,
  withItemBusy,
  updateItemQuantity,
  removeItem,
}: {
  sortedItems: CartItem[]
  busy: boolean
  activeItemId: string | null
  formatCurrency: (value: number) => string
  removeLabel: string
  withItemBusy: (itemId: string, operation: () => Promise<boolean>) => Promise<void>
  updateItemQuantity: (itemId: string, quantity: number) => Promise<boolean>
  removeItem: (itemId: string) => Promise<boolean>
}) => (
  <ul className="space-y-2">
    {sortedItems.map((item) => {
      const itemBusy = busy && activeItemId === item.itemId
      const imageUrl = resolveImage(item)
      const availableStock =
        typeof item.availableStockSnapshot === 'number' && Number.isFinite(item.availableStockSnapshot)
          ? Math.max(0, Math.trunc(item.availableStockSnapshot))
          : null
      const quantityLimitReached = availableStock !== null && item.quantity >= availableStock
      const increaseDisabled = itemBusy || quantityLimitReached
      return (
        <li key={item.itemId} className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
          <div className="grid grid-cols-[52px_1fr] gap-2.5">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={item.productName}
                className="h-14 w-14 rounded-lg border border-slate-200 bg-white object-cover"
                loading="lazy"
              />
            ) : (
              <div className="grid h-14 w-14 place-items-center rounded-lg border border-slate-200 bg-white text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                N/A
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">{item.productName}</p>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-700">
                <span>{formatCurrency(item.unitPriceSnapshot)}</span>
                <span className="font-semibold">{formatCurrency(item.lineTotal)}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={itemBusy}
                  className="grid h-7 w-7 place-items-center rounded-md border border-slate-300 bg-white text-sm font-semibold text-slate-700 disabled:opacity-60"
                  onClick={() => {
                    void withItemBusy(item.itemId, () =>
                      item.quantity <= 1
                        ? removeItem(item.itemId)
                        : updateItemQuantity(item.itemId, item.quantity - 1),
                    )
                  }}
                >
                  -
                </button>
                <span className="min-w-7 text-center text-xs font-semibold text-slate-900">
                  {item.quantity}
                </span>
                <button
                  type="button"
                  disabled={increaseDisabled}
                  aria-disabled={increaseDisabled}
                  className="grid h-7 w-7 place-items-center rounded-md border border-slate-300 bg-white text-sm font-semibold text-slate-700 disabled:opacity-60"
                  onClick={() => {
                    if (increaseDisabled) return
                    void withItemBusy(item.itemId, () =>
                      updateItemQuantity(item.itemId, item.quantity + 1),
                    )
                  }}
                >
                  +
                </button>
                <button
                  type="button"
                  disabled={itemBusy}
                  className="ml-auto rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700 disabled:opacity-60"
                  onClick={() => {
                    void withItemBusy(item.itemId, () => removeItem(item.itemId))
                  }}
                >
                  {removeLabel}
                </button>
              </div>
            </div>
          </div>
        </li>
      )
    })}
  </ul>
)
